import { Box, Button, Grid, Typography } from "@material-ui/core"
import { usePostConfirmarRedefinirSenha } from "api/touchone/requests/post-confirmar-redefinir-senha"
import { usePostRedefinirSenha } from "api/touchone/requests/post-redefinir-senha"
import { usePostValidarRedefinirSenha } from "api/touchone/requests/post-validar-redefinir-senha"
import { DefaultFormRefs } from "components/forms/utils"
import { InputPin } from "components/inputs/input-pin"
import { TextFieldSaurus } from "components/inputs/text-field-saurus"
import { useNotificacoes } from "components/notificacoes"
import { CircularLoading } from "components/utils/circular-loading"
import { RedefinirSenhaFormModel } from "models/app/forms/auth/redefinir-senha-form"
import React, { forwardRef, useImperativeHandle, useState } from "react"
import { validarDocumentoTelefoneEmail } from "utils/validar-documento-telefone-email"
import { useStyles } from "./redefinir-senha-styles"
import { Reenviarcodigo } from "./components/reenviar-codigo/reenviar-codigo"

export type TRedefinirSenhaProps = {
    setIsRedefinirSenha: (isRedefinirSenha: boolean) => void
}

export const RedefinirSenha = forwardRef<DefaultFormRefs<RedefinirSenhaFormModel>, TRedefinirSenhaProps>(({
    setIsRedefinirSenha,
    ...props
}: TRedefinirSenhaProps, refs) => {
    const [informacoes, setInformacoes] = useState<RedefinirSenhaFormModel>(new RedefinirSenhaFormModel())
    const [value, setValue] = useState<string>('')
    const [confirmarSenha, setConfirmarSenha] = useState<string>('')
    const [error, setError] = useState<{ campo: string, mensagem: string } | undefined>({ campo: '', mensagem: '' })
    const classes = useStyles()

    const { showErrorMessage, showSuccessMessage } = useNotificacoes()

    const { postRedefinirSenha, carregando: carregandoPostRedefinirSenha } = usePostRedefinirSenha()
    const { postValidarRedefinirSenha, carregando: carregandoPostValidarRedefinirSenha } = usePostValidarRedefinirSenha()
    const { postConfirmarRedefinirSenha, carregando: carregandoPostConfirmarRedefinirSenha } = usePostConfirmarRedefinirSenha()

    const carregando = carregandoPostRedefinirSenha || carregandoPostValidarRedefinirSenha || carregandoPostConfirmarRedefinirSenha

    const enviarEmailOuSms = async (info: RedefinirSenhaFormModel) => {
        const res = await postRedefinirSenha(info)
        if (res.erro) throw res.erro
        showSuccessMessage(
            info.email
                ? 'Email enviado com sucesso!'
                : 'SMS ou E-mail enviado com sucesso!'
        )
    }

    const validarCodigo = async (info: RedefinirSenhaFormModel) => {
        const res = await postValidarRedefinirSenha(info)
        if (res.erro) throw res.erro
        showSuccessMessage('Código válido!')
    }

    const confirmarRedefinirSenha = async (info: RedefinirSenhaFormModel) => {
        const res = await postConfirmarRedefinirSenha(info)
        if (res.erro) throw res.erro
        showSuccessMessage('Senha redefinida com sucesso!')
    }

    const handleSubmit = async () => {
        setError(undefined)
        try {
            let informacoesAtualizadas = informacoes

            if (!informacoes.email && !informacoes.telefone && !informacoes.cpfCnpj) {

                informacoesAtualizadas = { ...informacoes, ...validarDocumentoTelefoneEmail(value) }
                await enviarEmailOuSms(informacoesAtualizadas)
            } else if (!informacoes.redefinirCodigo) {
                if (value.length !== 6) {
                    setError({ campo: 'redefinirCodigo', mensagem: 'O código deve conter 6 dígitos' })
                    return
                }
                informacoesAtualizadas = { ...informacoes, redefinirCodigo: value }
                await validarCodigo(informacoesAtualizadas)
            } else {
                if (value.length < 6) {
                    setError({ campo: 'senha', mensagem: 'A senha deve conter pelo menos 6 caracteres' })
                    return
                }
                if (value !== confirmarSenha) {
                    setError({ campo: 'confirmarSenha', mensagem: 'As senhas não coincidem' })
                    return
                }
                informacoesAtualizadas = { ...informacoes, senha: value, senhaConfirmacao: confirmarSenha }
                await confirmarRedefinirSenha(informacoesAtualizadas)
                setIsRedefinirSenha(false)
                return
            }

            setInformacoes(informacoesAtualizadas)
            setValue('')
        } catch (error: any) {
            showErrorMessage(error.message)
        }
    }

    useImperativeHandle(refs, () => ({
        submitForm: async () => {
            await handleSubmit();
        },
        resetForm: () => {
        },
        fillForm: () => {
        },
    }));

    return (
        <Box
            component="form"
            onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
            }}
            p={2}
            alignItems="center"
            justifyContent="center"
            display="flex"
            height="100%"
        >
            {carregando && <CircularLoading tipo="FULLSIZED" />}
            <Grid container spacing={2}>
                {!informacoes.email && !informacoes.telefone && !informacoes.cpfCnpj ? (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="body1" color="textPrimary" align="center" className={classes.typography}>
                                Insira seu email/telefone ou CPF/CNPJ para enviarmos um email(ou SMS) para você redefinir ou ativar sua conta.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldSaurus
                                label="Email ou Telefone ou CPF/CNPJ"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                allowSubmit
                            />
                        </Grid>
                    </>
                ) : !informacoes.redefinirCodigo ? (
                    <>
                        <Grid item xs={12}>
                            <Typography align="center" variant="body1" color="textPrimary">
                                Insira o código que foi enviado para você.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <InputPin
                                canFocus={true}
                                lenght={6}
                                onChange={(e, value) => setValue(value)}
                                allowSubmit
                                error={error?.campo === 'redefinirCodigo'}
                                helperText={error?.campo === 'redefinirCodigo' ? error?.mensagem : undefined}
                                fullWidth={true}
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
                                <Reenviarcodigo onReenviar={async () => await enviarEmailOuSms(informacoes)} />
                            </Box>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Typography align="center" variant="body1" color="textPrimary">
                                Insira sua nova senha.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldSaurus
                                tipo="PASSWORD"
                                label="Senha"
                                error={error?.campo === 'senha'}
                                helperText={error?.campo === 'senha' ? error?.mensagem : undefined}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldSaurus
                                tipo="PASSWORD"
                                label="Confirmação de Senha"
                                error={error?.campo === 'confirmarSenha'}
                                helperText={error?.campo === 'confirmarSenha' ? error?.mensagem : undefined}
                                value={confirmarSenha}
                                onChange={(e) => setConfirmarSenha(e.target.value)}
                                allowSubmit
                            />
                        </Grid>
                    </>
                )}
                <Button
                    style={{ display: 'none' }}
                    type="submit"
                >
                </Button>
            </Grid>
        </Box>
    )
})
